import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Header from "../Header";
import {
  comboAdminList,
  comboAdminSave,
  comboAdminStatus,
  comboCatalog,
} from "../../api/comboApi";
import { providerLabel, formatRupees } from "../../utils/providers";
import { BROADBAND, ComboChips, comboLabel, errMsg } from "../Combo/comboUtils";
import "../Combo/combo.css";
import "./index.css";

// Providers a combo can charge for. VRPlay is handled separately: it is
// always a free bonus on top.
const PAID_PROVIDERS = [
  "internet",
  "dvpl",
  "railtel",
  "pioneeriptv",
  "watcho",
  "playboxtv",
  "etvwin",
];

// One-click starting points for the combinations currently on sale.
// ("OTT" here pre-selects Watcho; tick PlayBox TV instead if that is the
// OTT plan you want in the bundle.)
const TEMPLATES = [
  { label: "Internet + Pioneer IPTV", providers: ["internet", "pioneeriptv"] },
  { label: "DVPL + Pioneer IPTV", providers: ["dvpl", "pioneeriptv"] },
  { label: "RailTel + Pioneer IPTV", providers: ["railtel", "pioneeriptv"] },
  { label: "Internet + OTT + ETV Win", providers: ["internet", "watcho", "etvwin"] },
  { label: "Internet + Pioneer IPTV + OTT + ETV Win", providers: ["internet", "pioneeriptv", "watcho", "etvwin"] },
];

// "1 Month", "3 Months", "6M", "1 Year", "90 Days" -> months (1..12); same rule as the server
// (pioneer_lib.php pioneerMonthsFromText) for how long a combo's IPTV runs.
const monthsFromText = (text) => {
  const t = String(text || "").toLowerCase();
  let m = t.match(/(\d+)\s*(years?|yrs?)\b/);
  if (m) return Math.max(1, Math.min(12, 12 * Number(m[1])));
  m = t.match(/(\d+)\s*(months?|mon|mnths?|m)\b/);
  if (m) return Math.max(1, Math.min(12, Number(m[1])));
  m = t.match(/(\d+)\s*days?\b/);
  if (m) return Math.max(1, Math.min(12, Math.round(Number(m[1]) / 30)));
  if (t.includes("year") || t.includes("annual")) return 12;
  return 1;
};

/**
 * Suggested combo price from the plans picked: broadband sub plan price + Pioneer DPO price
 * (incl. FTA + GST) x the IPTV months (= the broadband sub plan's months) + paid OTT plans.
 */
const calcComboPrice = (picks, catalog) => {
  const parts = [];
  let months = 1;
  ["internet", "dvpl"].forEach((p) => {
    const pick = picks[p];
    if (!pick || !pick.plan_id || !pick.sub_plan_id) return;
    const plan = (catalog[p] || []).find((x) => String(x.id) === String(pick.plan_id));
    const sub = plan && (plan.sub_plans || []).find((x) => String(x.id) === String(pick.sub_plan_id));
    if (!sub) return;
    months = monthsFromText(sub.name);
    parts.push({ label: comboLabel(p), amount: Number(sub.price || 0) });
  });
  Object.keys(picks).forEach((p) => {
    if (["internet", "dvpl", "railtel"].includes(p)) return;
    const pick = picks[p];
    const plan = pick && pick.plan_id && (catalog[p] || []).find((x) => String(x.id) === String(pick.plan_id));
    if (!plan) return;
    const times = p === "pioneeriptv" ? months : 1;
    parts.push({ label: comboLabel(p), amount: Number(plan.price || 0) * times, times, unit: Number(plan.price || 0) });
  });
  const total = Math.round(parts.reduce((s, x) => s + x.amount, 0) * 100) / 100;
  return { total, parts, months };
};

const emptyForm = () => ({
  id: 0,
  name: "",
  description: "",
  base_price: "",
  price_auto: true, // follows the picked plans until the price is typed by hand
  is_active: true,
  picks: {}, // { provider: { plan_id, sub_plan_id } }
  vrplay: "", // free bonus plan id ("" = none)
});

const formFromCombo = (combo) => {
  const picks = {};
  let vrplay = "";
  combo.items.forEach((it) => {
    if (it.provider === "vrplay") {
      vrplay = String(it.plan_id);
    } else {
      picks[it.provider] = {
        plan_id: String(it.plan_id),
        sub_plan_id: it.sub_plan_id ? String(it.sub_plan_id) : "",
      };
    }
  });
  return {
    id: combo.id,
    name: combo.name,
    description: combo.description || "",
    base_price: String(combo.base_price),
    price_auto: false,
    is_active: !!combo.is_active,
    picks,
    vrplay,
  };
};

const ComboPlans = () => {
  const [combos, setCombos] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [unavailable, setUnavailable] = useState({});
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null = modal closed
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, cat] = await Promise.all([comboAdminList(), comboCatalog()]);
      setCombos(list.data.combos || []);
      setCatalog(cat.data.catalog || {});
      setUnavailable(cat.data.unavailable || {});
    } catch (err) {
      toast.error(errMsg(err, "Failed to load combos"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ── Suggested price from the picked plans ─────────────────────
  const calc = useMemo(() => (form ? calcComboPrice(form.picks, catalog) : null), [form, catalog]);
  useEffect(() => {
    if (form && form.price_auto && calc && calc.parts.length && String(calc.total) !== form.base_price) {
      setForm((f) => (f && f.price_auto ? { ...f, base_price: String(calc.total) } : f));
    }
  }, [form, calc]);

  // ── Builder helpers ───────────────────────────────────────────
  const setPick = (provider, patch) =>
    setForm((f) => ({
      ...f,
      picks: { ...f.picks, [provider]: { plan_id: "", sub_plan_id: "", ...f.picks[provider], ...patch } },
    }));

  const toggleProvider = (provider, on) =>
    setForm((f) => {
      const picks = { ...f.picks };
      if (on) picks[provider] = picks[provider] || { plan_id: "", sub_plan_id: "" };
      else delete picks[provider];
      return { ...f, picks };
    });

  const applyTemplate = (tpl) =>
    setForm((f) => {
      const picks = {};
      tpl.providers.forEach((p) => {
        picks[p] = f.picks[p] || { plan_id: "", sub_plan_id: "" };
      });
      const firstVr = (catalog.vrplay || [])[0];
      return { ...f, picks, vrplay: f.vrplay || (firstVr ? String(firstVr.id) : "") };
    });

  const problems = useMemo(() => {
    if (!form) return [];
    const out = [];
    const chosen = Object.keys(form.picks);
    if (!form.name.trim()) out.push("Enter a combo name.");
    if (form.base_price === "" || Number(form.base_price) < 0 || Number.isNaN(Number(form.base_price))) {
      out.push("Enter a base price (0 or more).");
    }
    if (chosen.length < 2) out.push("Choose at least two paid plans.");
    if (chosen.filter((p) => BROADBAND.includes(p)).length > 1) {
      out.push("Choose only one of Internet, DVPL or RailTel.");
    }
    chosen.forEach((p) => {
      const pick = form.picks[p];
      if (!pick.plan_id) out.push(`Select a plan for ${comboLabel(p)}.`);
      else if ((p === "internet" || p === "dvpl") && !pick.sub_plan_id) {
        out.push(`Select a sub-plan for ${comboLabel(p)}.`);
      }
    });
    return out;
  }, [form]);

  const pendingSetup = form
    ? Object.keys(form.picks).filter((p) => unavailable[p] && p !== "railtel")
    : [];

  const save = async () => {
    if (problems.length) {
      toast.warn(problems[0]);
      return;
    }
    const items = Object.keys(form.picks).map((p) => ({
      provider: p,
      plan_id: Number(form.picks[p].plan_id),
      sub_plan_id: form.picks[p].sub_plan_id ? Number(form.picks[p].sub_plan_id) : null,
    }));
    if (form.vrplay) items.push({ provider: "vrplay", plan_id: Number(form.vrplay) });

    setSaving(true);
    try {
      await comboAdminSave({
        id: form.id || undefined,
        name: form.name.trim(),
        description: form.description.trim(),
        base_price: Number(form.base_price),
        is_active: form.is_active,
        items,
      });
      toast.success(form.id ? "Combo updated" : "Combo created");
      setForm(null);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Failed to save combo"));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (combo, action) => {
    if (action === "delete" && !window.confirm(`Delete "${combo.name}"? This cannot be undone.`)) return;
    setBusyId(combo.id);
    try {
      await comboAdminStatus(combo.id, action);
      toast.success(
        action === "delete" ? "Combo deleted" : action === "activate" ? "Combo activated" : "Combo deactivated"
      );
      load();
    } catch (err) {
      toast.error(errMsg(err, "Action failed"));
    } finally {
      setBusyId(null);
    }
  };

  // ── Render ────────────────────────────────────────────────────
  const renderPlanSelect = (provider) => {
    const pick = form.picks[provider] || {};
    const options = catalog[provider] || [];

    if (provider === "internet" || provider === "dvpl") {
      const pkg = options.find((o) => String(o.id) === String(pick.plan_id));
      return (
        <div className="cmbp-selects">
          <select
            value={pick.plan_id || ""}
            onChange={(e) => setPick(provider, { plan_id: e.target.value, sub_plan_id: "" })}
            aria-label={`${comboLabel(provider)} package`}
          >
            <option value="">Select package</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
          <select
            value={pick.sub_plan_id || ""}
            disabled={!pkg}
            onChange={(e) => setPick(provider, { sub_plan_id: e.target.value })}
            aria-label={`${comboLabel(provider)} sub-plan`}
          >
            <option value="">Select sub-plan</option>
            {(pkg?.sub_plans || []).map((s) => (
              <option key={s.id} value={s.id}>{s.name} — {formatRupees(s.price)}</option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div className="cmbp-selects">
        <select
          value={pick.plan_id || ""}
          onChange={(e) => setPick(provider, { plan_id: e.target.value })}
          aria-label={`${comboLabel(provider)} plan`}
        >
          <option value="">Select plan</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name} — {formatRupees(o.price)}{o.duration ? ` · ${o.duration} days` : ""}
            </option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <>
      <Header />

      <div className="cmb-page">
        <div className="cmb-page__head">
          <div>
            <h1 className="cmb-page__title">Combo Plans</h1>
            <p className="cmb-page__sub">
              Bundle plans from different providers into one recharge. Each combo is then assigned,
              with a price, to the admins and operators who may sell it.
            </p>
          </div>
          <button type="button" className="cmb-btn cmb-btn--primary" onClick={() => setForm(emptyForm())}>
            + New combo
          </button>
        </div>

        {loading ? (
          <div className="cmb-empty">Loading combos…</div>
        ) : combos.length === 0 ? (
          <div className="cmb-empty">
            <strong>No combos yet</strong>
            Create the first one to start assigning it to admins and operators.
          </div>
        ) : (
          <div className="cmb-panel cmb-table-wrap">
            <table className="cmb-table">
              <thead>
                <tr>
                  <th>Combo</th>
                  <th>Plans</th>
                  <th className="cmb-num">Base price</th>
                  <th className="cmb-num">Assigned to</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {combos.map((c) => {
                  const notReady = c.items.filter((i) => !i.recharge_available && !i.is_free).map((i) => comboLabel(i.provider));
                  return (
                    <tr key={c.id}>
                      <td>
                        <strong>{c.name}</strong>
                        {c.description ? <div className="cmb-muted">{c.description}</div> : null}
                      </td>
                      <td>
                        <ComboChips items={c.items} />
                        {notReady.length > 0 && (
                          <div className="cmbp-warn">
                            Recharge not enabled yet for {notReady.join(", ")} — cannot be sold until it is.
                          </div>
                        )}
                      </td>
                      <td className="cmb-num">{formatRupees(c.base_price)}</td>
                      <td className="cmb-num">{c.assigned_count}</td>
                      <td>
                        <span className={`cmb-badge cmb-badge--${c.is_active ? "active" : "inactive"}`}>
                          {c.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <div className="cmb-actions">
                          <button type="button" className="cmb-btn cmb-btn--sm" onClick={() => setForm(formFromCombo(c))}>
                            Edit
                          </button>
                          <button
                            type="button"
                            className="cmb-btn cmb-btn--sm"
                            disabled={busyId === c.id}
                            onClick={() => changeStatus(c, c.is_active ? "deactivate" : "activate")}
                          >
                            {c.is_active ? "Deactivate" : "Activate"}
                          </button>
                          {c.recharge_count === 0 && (
                            <button
                              type="button"
                              className="cmb-btn cmb-btn--sm cmb-btn--danger"
                              disabled={busyId === c.id}
                              onClick={() => changeStatus(c, "delete")}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {form && (() => {
        const chosen = Object.keys(form.picks);
        const broadband = chosen.find((p) => BROADBAND.includes(p)) || "";
        const tvProviders = PAID_PROVIDERS.filter((p) => !BROADBAND.includes(p));
        const pickedLabel = (p) => {
          const pick = form.picks[p] || {};
          const plan = (catalog[p] || []).find((o) => String(o.id) === String(pick.plan_id));
          if (!plan) return null;
          if (p === "internet" || p === "dvpl") {
            const sub = (plan.sub_plans || []).find((x) => String(x.id) === String(pick.sub_plan_id));
            return sub ? `${plan.name} · ${sub.name}` : plan.name;
          }
          return plan.name;
        };
        const partFor = (p) => calc && calc.parts.find((x) => x.label === comboLabel(p));
        const vrPlan = (catalog.vrplay || []).find((o) => String(o.id) === String(form.vrplay));
        const priceDiffers = calc && calc.parts.length && String(calc.total) !== String(form.base_price);

        return (
        <div className="cmb-overlay" role="dialog" aria-modal="true" aria-label="Combo builder">
          <div className="cmb-modal cmbp-modal">
            <div className="cmb-modal__head">
              <div>
                <h3>{form.id ? "Edit combo" : "New combo"}</h3>
                <p className="cmbp-sub">Pick the plans, check the price, name it and save.</p>
              </div>
              <button type="button" className="cmb-modal__close" onClick={() => setForm(null)} aria-label="Close">×</button>
            </div>

            <div className="cmb-modal__body cmbp-body">
              <div className="cmbp-layout">
                <div className="cmbp-main">
                  {!form.id && (
                    <div className="cmbp-quick">
                      <span className="cmbp-quick__label">Quick start</span>
                      <div className="cmbp-templates">
                        {TEMPLATES.map((t) => {
                          const blocked = t.providers.some((p) => p === "railtel" && unavailable.railtel);
                          const isOn = t.providers.length === chosen.length && t.providers.every((p) => form.picks[p]);
                          return (
                            <button
                              key={t.label}
                              type="button"
                              className={`cmbp-chip ${isOn ? "is-on" : ""}`}
                              disabled={blocked}
                              title={blocked ? unavailable.railtel : "Pre-select these providers"}
                              onClick={() => applyTemplate(t)}
                            >
                              {t.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* 1. Broadband: one of Internet / DVPL / RailTel */}
                  <section className="cmbp-step">
                    <div className="cmbp-step__head">
                      <span className="cmbp-step__no">1</span>
                      <div>
                        <h4>Broadband</h4>
                        <p>Choose one connection. The IPTV months follow its sub plan.</p>
                      </div>
                    </div>
                    <div className="cmbp-seg" role="radiogroup" aria-label="Broadband">
                      {["", ...BROADBAND].map((p) => {
                        const locked = p === "railtel" && !!unavailable.railtel;
                        return (
                          <button
                            key={p || "none"}
                            type="button"
                            role="radio"
                            aria-checked={broadband === p}
                            className={`cmbp-seg__btn ${broadband === p ? "is-on" : ""}`}
                            disabled={locked}
                            title={locked ? unavailable.railtel : undefined}
                            onClick={() =>
                              setForm((f) => {
                                const picks = { ...f.picks };
                                BROADBAND.forEach((b) => { if (b !== p) delete picks[b]; });
                                if (p) picks[p] = picks[p] || { plan_id: "", sub_plan_id: "" };
                                return { ...f, picks };
                              })
                            }
                          >
                            {p ? comboLabel(p) : "None"}
                          </button>
                        );
                      })}
                    </div>
                    {broadband && <div className="cmbp-tile is-on cmbp-tile--flat">{renderPlanSelect(broadband)}</div>}
                    {unavailable.railtel && <div className="cmb-hint">RailTel: {unavailable.railtel}</div>}
                  </section>

                  {/* 2. TV & OTT */}
                  <section className="cmbp-step">
                    <div className="cmbp-step__head">
                      <span className="cmbp-step__no">2</span>
                      <div>
                        <h4>TV &amp; OTT</h4>
                        <p>Tap to add. Each added service needs a plan.</p>
                      </div>
                    </div>
                    <div className="cmbp-tiles">
                      {tvProviders.map((p) => {
                        const on = !!form.picks[p];
                        return (
                          <div key={p} className={`cmbp-tile ${on ? "is-on" : ""}`}>
                            <label className="cmbp-tile__top">
                              <input type="checkbox" checked={on} onChange={(e) => toggleProvider(p, e.target.checked)} />
                              <span className="cmbp-tile__name">{comboLabel(p)}</span>
                              {p === "pioneeriptv" && on && calc && calc.months > 1 && (
                                <span className="cmbp-tag">× {calc.months} months</span>
                              )}
                            </label>
                            {on && renderPlanSelect(p)}
                          </div>
                        );
                      })}
                    </div>
                  </section>

                  {/* 3. Free bonus */}
                  <section className="cmbp-step">
                    <div className="cmbp-step__head">
                      <span className="cmbp-step__no">3</span>
                      <div>
                        <h4>{providerLabel("vrplay")} <em className="cmbp-free">FREE</em></h4>
                        <p>Optional bonus, not charged.</p>
                      </div>
                    </div>
                    <div className="cmbp-selects">
                      <select value={form.vrplay} onChange={(e) => setForm({ ...form, vrplay: e.target.value })}
                        aria-label="Free VRPlay plan">
                        <option value="">No bonus</option>
                        {(catalog.vrplay || []).map((o) => (
                          <option key={o.id} value={o.id}>{o.name}{o.duration ? ` · ${o.duration} days` : ""}</option>
                        ))}
                      </select>
                    </div>
                  </section>

                  {/* 4. Name & status */}
                  <section className="cmbp-step">
                    <div className="cmbp-step__head">
                      <span className="cmbp-step__no">4</span>
                      <div>
                        <h4>Name &amp; status</h4>
                        <p>What operators see when they recharge.</p>
                      </div>
                    </div>
                    <div className="cmbp-grid">
                      <div className="cmb-field cmbp-span2">
                        <label htmlFor="cmb-name">Combo name</label>
                        <input id="cmb-name" type="text" value={form.name} maxLength={120}
                          onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Fibre 100 + Pioneer SD" />
                      </div>
                      <div className="cmb-field cmbp-span2">
                        <label htmlFor="cmb-desc">Description (optional)</label>
                        <input id="cmb-desc" type="text" value={form.description} maxLength={255}
                          onChange={(e) => setForm({ ...form, description: e.target.value })} />
                      </div>
                      <div className="cmb-field cmbp-span2">
                        <span className="cmbp-field-label">Status</span>
                        <div className="cmbp-seg cmbp-seg--sm" role="radiogroup" aria-label="Status">
                          <button type="button" role="radio" aria-checked={form.is_active}
                            className={`cmbp-seg__btn ${form.is_active ? "is-on" : ""}`}
                            onClick={() => setForm({ ...form, is_active: true })}>Active</button>
                          <button type="button" role="radio" aria-checked={!form.is_active}
                            className={`cmbp-seg__btn ${!form.is_active ? "is-on" : ""}`}
                            onClick={() => setForm({ ...form, is_active: false })}>Inactive</button>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>

                {/* Summary + price */}
                <aside className="cmbp-summary" aria-label="Combo summary">
                  <div className="cmbp-summary__inner">
                  <h4>Summary</h4>
                  {chosen.length === 0 && !form.vrplay ? (
                    <p className="cmbp-summary__empty">No plans picked yet.</p>
                  ) : (
                    <ul className="cmbp-lines">
                      {[...BROADBAND, ...tvProviders].filter((p) => form.picks[p]).map((p) => {
                        const part = partFor(p);
                        const label = pickedLabel(p);
                        return (
                          <li key={p}>
                            <div>
                              <strong>{comboLabel(p)}</strong>
                              <span className={label ? "" : "is-missing"}>{label || "Plan not selected"}</span>
                            </div>
                            <b>
                              {part
                                ? `${formatRupees(part.unit !== undefined ? part.unit : part.amount)}${part.times > 1 ? ` × ${part.times}` : ""}`
                                : "—"}
                            </b>
                          </li>
                        );
                      })}
                      {form.vrplay && (
                        <li>
                          <div>
                            <strong>{providerLabel("vrplay")}</strong>
                            <span>{vrPlan ? vrPlan.name : ""}</span>
                          </div>
                          <b className="cmbp-free">FREE</b>
                        </li>
                      )}
                    </ul>
                  )}

                  <div className="cmbp-total">
                    <span>Calculated</span>
                    <strong>{calc && calc.parts.length ? formatRupees(calc.total) : "—"}</strong>
                  </div>

                  <div className="cmb-field">
                    <label htmlFor="cmb-price">Base price (₹)</label>
                    <input id="cmb-price" className="cmbp-price" type="number" min="0" step="0.01" value={form.base_price}
                      onChange={(e) => setForm({ ...form, base_price: e.target.value, price_auto: false })} />
                    {form.price_auto ? (
                      <span className="cmb-hint">Follows the plans you pick until you type a price.</span>
                    ) : priceDiffers ? (
                      <button type="button" className="cmb-btn cmb-btn--sm cmbp-use"
                        onClick={() => setForm({ ...form, base_price: String(calc.total), price_auto: true })}>
                        Use calculated {formatRupees(calc.total)}
                      </button>
                    ) : null}
                    <span className="cmb-hint">Starting price when this combo is assigned to admins / operators.</span>
                  </div>

                  {pendingSetup.length > 0 && (
                    <div className="cmb-note cmb-note--warn">
                      Recharge for <strong>{pendingSetup.map(comboLabel).join(", ")}</strong> is not enabled for
                      combos yet. You can save now; it can be sold once that provider is switched on.
                    </div>
                  )}

                  {problems.length > 0 ? (
                    <ul className="cmbp-todo">
                      {problems.map((m) => <li key={m}>{m}</li>)}
                    </ul>
                  ) : (
                    <div className="cmbp-ready">Ready to save</div>
                  )}
                  </div>
                </aside>
              </div>
            </div>

            <div className="cmb-modal__foot">
              <button type="button" className="cmb-btn" onClick={() => setForm(null)} disabled={saving}>Cancel</button>
              <button type="button" className="cmb-btn cmb-btn--primary" onClick={save} disabled={saving || problems.length > 0}>
                {saving ? "Saving…" : form.id ? "Save changes" : "Create combo"}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </>
  );
};

export default ComboPlans;
